import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import styles from "./Overview.module.css";

import {
  getModules,
  getModulesByType,
} from "../../redux/reducers/ModuleReducer";
import { useTypedSelector } from "../../redux/reducers/RootReducer";
import { getUserCredential } from "../../redux/reducers/LocalUserReducer";
import { getModuleStateById } from "./../../redux/reducers/ModuleStateReducer";
import { isAuthenticationEnable } from "../../redux/reducers/AuthenticationSettingReducer";

import { deleteUserCredential } from "../../redux/actions/LocalUserActions";

import { EUserRole, User } from "../../Model/User";
import { EImagingMode } from "../../Model/EImagingMode";
import { EModuleType } from "../../Model/EModuleType";

import SuiteApi from "./../../api/SuiteApi";
import UserApi from "../../api/UserApi";

import IconEditUser from "../../Icons/Command/EditStatus";
import LogoutIcon from "../../Icons/TopCommandPanel/Logout";
import ManageUsersIcon from "../../Icons/SideMenu/UserManagement";

import Header from "../Header/Header";
import LayoutContent from "../LayoutContent";
import ImagerStatus from "./ImagerStatus";
import LoadportStatus from "./LoadportStatus";
import PsrStatus from "./PsrStatus";
import ChamberStatus from "./ChamberStatus";
import SuiteStatus from "./SuiteStatus";
import TemperatureStatus from "./TemperatureStatus";
import ImagingModeModal from "./ImagingModeModal";

import RipStatus from "./RipStatus";
import Breadcrumb from "../Header/Breadcrumb";
import HeaderButton from "../Header/HeaderButton";
import HeaderDropDownButton from "../Header/HeaderDropDownButton";
import UserProfile from "./UserProfile";
import EditProfileModal from "./EditProfileModal";

import ImagingAutoMode from "../../Icons/TopCommandPanel/AutoModeTop";
import ImagingManualMode from "../../Icons/TopCommandPanel/ManualModeTop";
import ScheduleStatus from "./ScheduleStatus/ScheduleStatus";

const EDIT_PROFILE = "EDIT_PROFILE";
const MANAGE_USERS = "MANAGE_USERS";
const LOGOUT = "LOGOUT";

const DashboardPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const userCredential = useTypedSelector((state) => getUserCredential(state));
  const moduleStateById = useTypedSelector((state) =>
    getModuleStateById(state)
  );

  const modules = useTypedSelector((state) => getModules(state));
  const moduleByType = useTypedSelector((state) => getModulesByType(state));
  const isAuthEnable: boolean = useTypedSelector((state) =>
    isAuthenticationEnable(state)
  );

  const suiteModule = moduleByType[EModuleType.SUITE][0];
  const suiteState = moduleStateById[suiteModule.identifier].suite;
  if (suiteState == null) throw new Error("Suite state is null");

  const [showChangeMode, setShowChangeMode] = useState(false);
  const [showEditUserPopUp, setShowEditUserPopUp] = useState(false);
  const [user, setUser] = useState({} as User);

  useEffect(() => {
    UserApi.getUsers(
      null,
      userCredential?.userName,
      null,
      null,
      null,
      null
    ).then((result) => {
      if (result.length > 0) setUser(result[0]);
    });
  }, [userCredential]);

  let moduleStatusViews: React.ReactNode[] = [
    <TemperatureStatus key={EModuleType.CHILLER} />,
    <ScheduleStatus key={"SCHEDULE_CARD"} />,
  ];
  for (const module of modules) {
    switch (module.moduleType) {
      case EModuleType.SUITE:
        moduleStatusViews.push(
          <SuiteStatus key={EModuleType.SUITE} module={module} />
        );
        break;
      case EModuleType.IMAGER:
        moduleStatusViews.push(
          <ImagerStatus key={EModuleType.IMAGER} module={module} />
        );
        break;
      case EModuleType.CHAMBER:
        moduleStatusViews.push(
          <ChamberStatus key={EModuleType.CHAMBER} module={module} />
        );
        break;
      case EModuleType.LOADPORT:
        moduleStatusViews.push(
          <LoadportStatus key={EModuleType.LOADPORT} module={module} />
        );
        break;
      case EModuleType.PSR:
        moduleStatusViews.push(
          <PsrStatus key={EModuleType.PSR} module={module} />
        );
        break;
      case EModuleType.RIP:
        moduleStatusViews.push(
          <RipStatus key={EModuleType.RIP} module={module} />
        );
        break;
    }
  }

  console.log("OVERVIEW", { moduleStatusViews });

  const onChangeMode = (id: string) => {
    switch (id) {
      case EImagingMode.Automatic.toString():
        setShowChangeMode(true);
        break;
      case EImagingMode.Manual.toString():
        SuiteApi.setImagingMode(EImagingMode.Automatic);
        break;
      default:
        break;
    }
  };

  const onProfileButtonClicked = (id: string) => {
    switch (id) {
      case EDIT_PROFILE:
        setShowEditUserPopUp(true);
        break;
      case MANAGE_USERS:
        history.push("/report-support/user-management");
        break;
      case LOGOUT:
        UserApi.logout();
        dispatch(deleteUserCredential());
        break;
    }
  };

  const getProfileName = () => {
    if (user.name !== undefined && user.name !== null && user.name !== "") {
      return user.name;
    }
    return userCredential ? userCredential.userName : "";
  };

  const getProfileDropDown = () => {
    const dropdown = [
      {
        value: EDIT_PROFILE,
        display: "Edit Profile",
        icon: <IconEditUser />,
        disabled: false,
      },
    ];

    if (
      userCredential?.role === EUserRole.Administrator ||
      userCredential?.role === EUserRole.Support
    ) {
      dropdown.push({
        value: MANAGE_USERS,
        display: "Manage Users",
        icon: <ManageUsersIcon />,
        disabled: false,
      });
    }

    dropdown.push({
      value: LOGOUT,
      display: "Logout",
      icon: <LogoutIcon />,
      disabled: false,
    });
    return dropdown;
  };

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[{ title: "System Overview", to: "/", disabled: false }]}
          />
          <Header.RightSide>
            {suiteState.imagingMode === EImagingMode.Automatic && (
              <HeaderButton
                id={EImagingMode.Automatic.toString()}
                onClick={onChangeMode}
                label="Switch to Manual Mode"
                icon={<ImagingManualMode />}
              />
            )}
            {suiteState.imagingMode === EImagingMode.Manual && (
              <HeaderButton
                id={EImagingMode.Manual.toString()}
                onClick={onChangeMode}
                label="Switch to Auto Mode"
                icon={<ImagingAutoMode />}
              />
            )}
            {isAuthEnable && (
              <HeaderDropDownButton
                id={"USER_PROFILE"}
                label={getProfileName()}
                icon={<UserProfile user={user} />}
                onChange={onProfileButtonClicked}
                iconDisplayValues={getProfileDropDown()}
                disabled={false}
                dropDownStyle={{
                  left: "-128px",
                }}
              />
            )}
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.Content
        style={{
          paddingLeft: "0px",
          paddingTop: "0px",
          backgroundColor: "#B4B4B4",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            height: 73,
            backgroundColor: "white",
            textAlign: "center",
            fontFamily: "Raleway",
            fontStyle: "normal",
            fontWeight: "normal",
            fontSize: 24,
            lineHeight: 28,
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div>
            Welcome to ROCK IMAGER<sup>®</sup>!
          </div>
        </div>
        <div className={styles.overviewContent}>
          {moduleStatusViews?.map((card) => card)}
        </div>
        <ImagingModeModal
          show={showChangeMode}
          onHide={() => setShowChangeMode(false)}
          name="overview"
        />
        <EditProfileModal
          show={showEditUserPopUp}
          onHide={() => setShowEditUserPopUp(false)}
          user={user}
          onChangeUser={(newUser) => setUser(newUser)}
        />
      </LayoutContent.Content>
    </LayoutContent>
  );
};

export default DashboardPage;
